<script lang="ts">
import {
  defineComponent, ref, onUnmounted, PropType, toRef, watch,
} from 'vue';
import { map } from 'lodash';
import { ImageEnhancementOutputs } from 'vue-media-annotator/use/useImageEnhancements';
import { StitchedSide, stitchedFrame } from 'vue-media-annotator/stitchedStereo';
import { SetTimeFunc } from '../../use/useTimeObserver';
import AnnotatorImageCursor from './AnnotatorImageCursor.vue';
import useAnnotatorImageCursor from './useAnnotatorImageCursor';
import { injectCameraInitializer } from './useMediaController';

export interface ImageDataItem {
  url: string;
  filename: string;
}
interface ImageDataItemInternal extends ImageDataItem {
  image: HTMLImageElement;
  cached: boolean; // true if onloadPromise has resolved
  frame: number; // frame number this image belongs to
  onloadPromise: Promise<boolean>;
}
function loadImageFunc(imageDataItem: ImageDataItem, img: HTMLImageElement) {
  // eslint-disable-next-line no-param-reassign
  img.src = imageDataItem.url;
}
export default defineComponent({
  name: 'ImageAnnotator',
  components: { AnnotatorImageCursor },
  props: {
    imageData: {
      type: Array as PropType<ImageDataItem[]>,
      required: true,
    },
    frameRate: {
      type: Number,
      required: true,
    },
    updateTime: {
      type: Function as PropType<SetTimeFunc>,
      required: true,
    },
    loadImageFunc: {
      type: Function as PropType<(imageDataItem: ImageDataItem, img: HTMLImageElement) => void>,
      default: loadImageFunc,
    },
    camera: {
      type: String as PropType<string>,
      default: 'singleCam',
    },
    imageEnhancementOutputs: {
      type: Object as PropType<ImageEnhancementOutputs>,
      default: () => ({
        brightness: { slope: 1, intercept: 0 },
        contrast: { slope: 1, intercept: 0.5 },
        saturation: { values: 1 },
        sharpen: { kernelMatrix: '0 -1 0 -1 5 -1 0 -1 0', divisor: 1 },
      }),
    },
    isDefaultImage: {
      type: Boolean as PropType<boolean>,
      required: true,
    },
    filterId: {
      type: String as PropType<string>,
      default: 'imageEnhancements',
    },
    /** Show only this half of each image (stitched stereo). */
    stitchedSide: {
      type: String as PropType<StitchedSide | null>,
      default: null,
    },
  },
  setup(props, { emit }) {
    const loadingVideo = ref(false);
    const loadingImage = ref(true);
    const cameraInitializer = injectCameraInitializer();
    const {
      state: data,
      geoViewer,
      cursorHandler,
      imageCursor,
      container,
      initializeViewer,
      mediaController,
      externallyDriven,
    } = cameraInitializer(props.camera, 'image-sequence', {
      // allow hoisting for these functions to pass a reference before defining them.
      seek, pause, play, setVolume: unimplemented, setSpeed: unimplemented,
    });
    const { playbackCursor } = useAnnotatorImageCursor(
      toRef(data, 'imageCursor'),
      toRef(data, 'cursor'),
      toRef(data, 'imageCursorEditing'),
    );
    data.maxFrame = props.imageData.length - 1;
    data.filenames = map(props.imageData, 'filename');
    // Below are configuration settings we can set until we decide on good numbers to utilize.
    let local = {
      playCache: 1, // seconds required to be fully cached before playback
      cacheSeconds: 6, // seconds to cache from the current frame
      frontBackRatio: 0.9, // 90% forward frames, 10% backward frames when caching
      imgs: new Array<ImageDataItemInternal | undefined>(props.imageData.length),
      pendingImgs: new Set<ImageDataItemInternal>(),
      lastFrame: -1,
      width: 0,
      height: 0,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      quadFeature: undefined as any,
    };
    function forceUnload(imgInternal: ImageDataItemInternal) {
      // Removal from list indicates we are no longer attempting to load this image
      local.imgs[imgInternal.frame] = undefined;
      // unset src to cancel outstanding load request
      // eslint-disable-next-line no-param-reassign
      imgInternal.image.src = '';
      local.pendingImgs.delete(imgInternal);
    }
    /**
     * When the component is unmounted, cancel all outstanding
     * requests for image load.
     */
    onUnmounted(() => Array.from(local.pendingImgs).forEach(forceUnload));
    /**
     * expectFrame when you know local.imgs[i] should not be undefined
     */
    function expectFrame(i: number) {
      const imgInternal = local.imgs[i];
      if (!imgInternal) {
        throw new Error(`imgs ${i} was undefined after assignment.`);
      }
      return imgInternal;
    }
    function failedToLoad(imgInternal: ImageDataItemInternal, loaded: boolean) {
      if (loaded || local.imgs[imgInternal.frame] !== imgInternal) {
        return false;
      }
      loadingImage.value = false;
      emit('load-error', `Could not load ${imgInternal.filename}. The file may have been moved or deleted.`);
      return true;
    }
    /**
     * Draw image to the GeoJS map, and update the map dimensions if they have changed.
     */
    function drawImage(img: HTMLImageElement) {
      if (!data.ready) {
        return;
      }
      if (img.naturalHeight > 40960 || img.naturalWidth > 40960) {
        // Warn about large images and conversion if possible
        emit('large-image-warning', true);
      }
      const frame = stitchedFrame(props.stitchedSide, img.naturalWidth, img.naturalHeight);
      if (
        frame.width > 0
        && frame.height > 0
        && ((frame.width !== local.width) || (frame.height !== local.height))
      ) {
        /**
         * Only update dimensions if the image has loaded
         * AND the dimensions have changed
         */
        local.width = frame.width;
        local.height = frame.height;
        mediaController.resetMapDimensions(local.width, local.height);
      }
      local.quadFeature
        .data([
          {
            ul: { x: 0, y: 0 },
            lr: { x: local.width, y: local.height },
            image: img,
            ...(frame.crop ? { crop: frame.crop } : {}),
          },
        ])
        .draw();
      data.imageRevision += 1;
    }
    function initializeFrameViewer(img: HTMLImageElement) {
      const frame = stitchedFrame(props.stitchedSide, img.naturalWidth, img.naturalHeight);
      initializeViewer(frame.width, frame.height);
    }
    /**
     * Adds a single frame to the pendingImgs array for loading and assigns it to the main
     * imgs list. Once the image is loaded it is removed from the pendingImgs
     * @param {int} i the image to cache if it isn't already assigned
     */
    function cacheFrame(i: number): ImageDataItemInternal {
      const { imgs, pendingImgs } = local;
      if (!imgs[i]) {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        const newImgInternal = {
          ...props.imageData[i],
          frame: i,
          image: img,
          cached: false,
          onloadPromise: new Promise<boolean>((resolve) => {
            img.onload = () => {
              const imgInternal = expectFrame(i);
              pendingImgs.delete(imgInternal);
              imgInternal.cached = true;
              resolve(true);
            };
            img.onerror = () => resolve(false);
          }),
        };
        imgs[i] = newImgInternal;
        pendingImgs.add(newImgInternal);
        props.loadImageFunc(newImgInternal, img);
      }
      return expectFrame(i);
    }
    /**
     * Caches a new range of frames to load in a forward->back pattern from the current frame
     * This allows for easily seeking backwards after seeking initially
     * @param {int} min lower bound frame number for caching
     * @param {int} max upper bound frame number for caching
     */
    function cacheNewRange(min: number, max: number) {
      for (let i = data.frame; i <= max; i += 1) {
        cacheFrame(i);
        const minusFrame = data.frame - (i - data.frame);
        if (minusFrame >= min) {
          cacheFrame(minusFrame);
        }
      }
    }
    /**
     * Begins loading a set of images around the current frame.  If the image is not playing
     * it will give priority tothe currently loaded frame
     */
    async function cacheImages() {
      const cachedFrames = local.cacheSeconds * props.frameRate;
      const min = Math.floor(Math.max(0, data.frame - cachedFrames * (1 - local.frontBackRatio)));
      const max = Math.ceil(
        Math.min(data.frame + local.frontBackRatio * cachedFrames, data.maxFrame),
      );
      const frameDiff = Math.abs(data.frame - local.lastFrame);
      const prevFrame = (data.frame < local.lastFrame);
      local.pendingImgs.forEach((imgInternal) => {
        // the current loading cache needs to be wiped out if we seek forward, backwards or
        // if we are out of the current range of the cache
        if (imgInternal.frame < min
          || imgInternal.frame > max
          || frameDiff > 1 || prevFrame
          || (!data.playing && frameDiff === 1)
        ) {
          forceUnload(imgInternal);
        }
      });
      let result = true;
      // if not playing we want the seeked to frame immediately and prevent caching until loaded
      if (!data.playing && !local.imgs[data.frame] && data.frame > 0) {
        result = await cacheFrame(data.frame)?.onloadPromise;
      }
      // Cache a new range of images based on current frame
      if (result) {
        cacheNewRange(min, max);
      }
    }
    async function seek(f: number | undefined) {
      if (!data.ready) {
        return;
      }
      if (f === undefined) {
        // No frame for this camera at the current aligned-timeline slot: blank
        // the pane. Deliberately leaves data.frame/data.filename untouched --
        // those are read elsewhere (e.g. annotation-overlay lookups) and this
        // phase doesn't touch annotation storage.
        data.hasFrame = false;
        if (local.quadFeature !== undefined) {
          local.quadFeature.layer().node().css('visibility', 'hidden');
        }
        return;
      }
      if (!data.hasFrame) {
        data.hasFrame = true;
        if (local.quadFeature !== undefined) {
          local.quadFeature.layer().node().css('visibility', '');
        }
      }
      let newFrame = f;
      if (f < 0) newFrame = 0;
      if (f > data.maxFrame) newFrame = data.maxFrame;
      local.lastFrame = data.frame;
      data.frame = newFrame;
      data.syncedFrame = newFrame;
      data.filename = props.imageData[data.frame].filename;
      if (data.frame !== 0 && local.lastFrame === data.frame) {
        return;
      }
      props.updateTime(data);
      cacheImages();
      const imgInternal = expectFrame(newFrame);
      drawImage(imgInternal.image);
      if (!imgInternal.cached) {
        loadingImage.value = true;
        // else wait for it to load
        const loaded = await imgInternal.onloadPromise;
        if (imgInternal.frame === data.frame && !failedToLoad(imgInternal, loaded)) {
          loadingImage.value = false;
          // if the seek hasn't changed since the image completed loading, draw it.
          drawImage(imgInternal.image);
        }
      } else {
        loadingImage.value = false;
      }
    }
    function pause() {
      data.playing = false;
      loadingVideo.value = false;
      props.updateTime(data);
    }
    /**
     * Checks to see if there are enough cached images to play for X seconds.
     * @param frame start frame to look for.
     * @param seconds num seconds to look for cache
     * @returns Promise to await for caching.
     */
    function checkCached(frame: number, seconds: number) {
      const upper = Math.min(frame + (seconds * props.frameRate), data.maxFrame);
      return local.imgs.slice(frame, upper)
        .filter((img) => img?.cached === false)
        .map((img) => img?.onloadPromise);
    }
    /**
     * Handles playback of the image sequence
     * Image playback is based on framerate but will pause and wait for images to load
     * if the currently accessed image is not loaded during playback.
     */
    async function syncWithVideo(nextFrame: number): Promise<void> {
      if (data.playing) {
        if (nextFrame > data.maxFrame) {
          return pause();
        }
        // expectFrame is safe here because, even though this frame may never have been
        // seeked before, it is at MOST 1 frame away from a frame that has.
        // The correct behavior of this function implicitly requires that seek()
        // always trigger caching for surrounding frames.
        const nextImage = expectFrame(nextFrame);
        if (!nextImage.cached) {
          // Prevents advancing the frame while playing if the current image is not loaded
          loadingVideo.value = true;
          await Promise.all(checkCached(nextFrame, local.playCache));
          loadingVideo.value = false;
          // A user interaction (pause, seek) could have happened during load.
          // Restart syncWithVideo() logic on same frame.  MUST return here to
          // prevent duplicating the loop.
          return syncWithVideo(data.frame + 1);
        }
        seek(nextFrame);
        setTimeout(() => syncWithVideo(data.frame + 1), 1000 / props.frameRate);
      }
      return undefined;
    }
    async function play() {
      try {
        data.playing = true;
        // When a global aligned timeline is driving playback, the aggregate
        // controller's own centralized tick calls seek() directly -- this
        // camera must not also free-run its own loop.
        if (!externallyDriven.value) {
          syncWithVideo(data.frame + 1);
        }
        props.updateTime(data);
      } catch (ex) {
        console.error(ex);
      }
    }
    function unimplemented() {
      throw new Error('Method unimplemented!');
    }
    watch(
      () => props.isDefaultImage,
      () => {
        if (local.quadFeature !== undefined) {
          if (props.isDefaultImage) {
            local.quadFeature.layer().node().css('filter', '');
          } else {
            local.quadFeature.layer().node().css('filter', `url(#${props.filterId})`);
          }
          data.imageRevision += 1;
        }
      },
      { deep: true },
    );

    if (local.imgs.length) {
      const imgInternal = cacheFrame(0);
      imgInternal.onloadPromise.then(async (loaded) => {
        if (imgInternal.frame !== data.frame || failedToLoad(imgInternal, loaded)) {
          return;
        }
        try {
          await imgInternal.image.decode();
        } catch (error) {
          if (imgInternal.frame !== data.frame) {
            return;
          }
          loadingImage.value = false;
          emit('load-error', `Could not display ${imgInternal.filename}. Its resolution may be too large for this browser or hardware.`, true);
          return;
        }
        if (imgInternal.frame !== data.frame) {
          return;
        }
        initializeFrameViewer(imgInternal.image);
        const quadFeatureLayer = geoViewer.value.createLayer('feature', {
          features: ['quad'],
          autoshareRenderer: false,
          renderer: 'canvas',
        });
        // Set quadFeature and conditionally apply brightness filter
        local.quadFeature = quadFeatureLayer.createFeature('quad');
        local.quadFeature.layer().node().css('filter', `url(#${props.filterId})`);
        data.ready = true;
        seek(0);
      });
    }
    function init() {
      data.maxFrame = props.imageData.length - 1;
      data.filenames = map(props.imageData, 'filename');
      // When the viewer already exists, an imageData change is a URL swap for
      // the same camera (e.g. the percentile-stretch display remap). Rebuild
      // only the image cache and redraw the current frame in place: calling
      // initializeViewer again would replace the geojs map, orphaning every
      // feature layer and event handler that other components (LayerManager
      // and its annotation/calibration layers) attached to the old map. Any
      // dimension change is already handled by drawImage/resetMapDimensions.
      const reusableQuad = data.ready && geoViewer.value !== undefined
        ? local.quadFeature : undefined;
      local = {
        playCache: 1, // seconds required to be fully cached before playback
        cacheSeconds: 6, // seconds to cache from the current frame
        frontBackRatio: 0.9, // 90% forward frames, 10% backward frames when caching
        imgs: new Array<ImageDataItemInternal | undefined>(props.imageData.length),
        pendingImgs: new Set<ImageDataItemInternal>(),
        lastFrame: -1,
        width: reusableQuad !== undefined ? local.width : 0,
        height: reusableQuad !== undefined ? local.height : 0,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        quadFeature: reusableQuad as any,
      };
      if (!local.imgs.length) {
        return;
      }
      if (reusableQuad !== undefined) {
        if (data.frame > data.maxFrame) {
          data.frame = data.maxFrame;
          data.syncedFrame = data.maxFrame;
        }
        data.filename = props.imageData[data.frame].filename;
        // seek() short-circuits on a same-frame re-seek, so drive the redraw
        // directly through the fresh cache.
        cacheImages();
        const imgInternal = expectFrame(data.frame);
        drawImage(imgInternal.image);
        if (!imgInternal.cached) {
          loadingImage.value = true;
          imgInternal.onloadPromise.then((loaded) => {
            if (imgInternal.frame === data.frame && !failedToLoad(imgInternal, loaded)) {
              loadingImage.value = false;
              drawImage(imgInternal.image);
            }
          });
        } else {
          loadingImage.value = false;
        }
        return;
      }
      const imgInternal = cacheFrame(0);
      imgInternal.onloadPromise.then((loaded) => {
        if (failedToLoad(imgInternal, loaded)) {
          return;
        }
        initializeFrameViewer(imgInternal.image);
        const quadFeatureLayer = geoViewer.value.createLayer('feature', {
          features: ['quad'],
          autoshareRenderer: false,
          renderer: 'canvas',
        });
        // Set quadFeature and conditionally apply brightness filter
        local.quadFeature = quadFeatureLayer.createFeature('quad');
        data.ready = true;
        seek(0);
        if (!props.isDefaultImage) {
          local.quadFeature.layer().node().css('filter', `url(#${props.filterId})`);
        }
      });
    }
    // Watch imageData for change
    watch(toRef(props, 'imageData'), () => {
      init();
    });
    return {
      data,
      loadingVideo,
      loadingImage,
      playbackCursor,
      imageCursorRef: imageCursor,
      containerRef: container,
      cursorHandler,
    };
  },
});
</script>

<template>
  <div class="video-annotator">
    <svg
      width="0"
      height="0"
      style="position: absolute; top: -1px; left: -1px"
    >
      <defs>
        <filter :id="filterId">
          <feComponentTransfer id="feBrightness">
            <feFuncR
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
            <feFuncG
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
            <feFuncB
              type="linear"
              :slope="imageEnhancementOutputs.brightness.slope"
              :intercept="imageEnhancementOutputs.brightness.intercept"
            />
          </feComponentTransfer>
          <!-- Contrast -->
          <feComponentTransfer id="feContrast">
            <feFuncR
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
            <feFuncG
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
            <feFuncB
              type="linear"
              :slope="imageEnhancementOutputs.contrast.slope"
              :intercept="imageEnhancementOutputs.contrast.intercept"
            />
          </feComponentTransfer>
          <!-- Saturation -->
          <feColorMatrix
            id="feSaturate"
            type="saturate"
            :values="imageEnhancementOutputs.saturation.values.toString()"
          />
          <!-- Sharpening -->
          <feConvolveMatrix
            id="feSharpen"
            order="3"
            :divisor="imageEnhancementOutputs.sharpen.divisor"
            :kernelMatrix="imageEnhancementOutputs.sharpen.kernelMatrix"
            edgeMode="duplicate"
          />
          <feComposite in2="SourceGraphic" operator="in" />
        </filter>
      </defs>
    </svg>
    <div
      ref="imageCursorRef"
      class="imageCursor"
    >
      <AnnotatorImageCursor
        :image-cursor="data.imageCursor"
        :image-cursor-editing="data.imageCursorEditing"
        :cursor="data.cursor"
      />
    </div>
    <div
      ref="containerRef"
      class="playback-container"
      :style="{ cursor: playbackCursor }"
      @mousemove="cursorHandler.handleMouseMove"
      @mouseleave="cursorHandler.handleMouseLeave"
      @mouseover="cursorHandler.handleMouseEnter"
    >
      <div class="loadingSpinnerContainer">
        <v-progress-circular
          v-if="loadingVideo || loadingImage"
          class="loadingSpinner"
          indeterminate
          size="100"
          width="15"
          color="light-blue"
        >
          Loading
        </v-progress-circular>
      </div>
      <div
        v-if="data.ready && !data.hasFrame"
        class="no-frame-overlay"
      >
        No frame at this instant
      </div>
    </div>
    <slot v-if="data.ready" />
  </div>
</template>

<style lang="scss" scoped>
@import "./annotator.scss";
</style>
